import { z } from 'zod';
import type { LimitiSetpoint } from './impianto';

/** Passo di regolazione del setpoint. */
export const PASSO_SETPOINT = 0.5;

export type EsitoValidazione =
  | { ok: true }
  | { ok: false; motivo: 'fuori_limiti'; limiti: LimitiSetpoint }
  | { ok: false; motivo: 'non_numerico' };

/**
 * RF-10b: un setpoint fuori dai limiti configurati dal tecnico non viene
 * accettato. Validato sia dall'interfaccia (per spiegare il limite) sia dalla
 * centralina (per farlo valere).
 */
export function validaSetpoint(valore: number, limiti: LimitiSetpoint): EsitoValidazione {
  if (!Number.isFinite(valore)) return { ok: false, motivo: 'non_numerico' };
  if (valore < limiti.min || valore > limiti.max) return { ok: false, motivo: 'fuori_limiti', limiti };
  return { ok: true };
}

export function arrotondaSetpoint(valore: number): number {
  return Math.round(valore / PASSO_SETPOINT) * PASSO_SETPOINT;
}

export const TaraturaSchema = z.object({
  offset: z.number().min(-10).max(10),
  fattore: z.number().min(0.5).max(1.5),
});

export const ParametriRegolazioneSchema = z.object({
  curvaClimatica: z.object({
    pendenza: z.number().min(0.1).max(3),
    temperaturaBase: z.number().min(10).max(40),
    temperaturaEsternaProgetto: z.number().min(-30).max(10),
    mandataMin: z.number().min(15).max(50),
    mandataMax: z.number().min(20).max(80),
  }),
  isteresi: z.object({
    zona: z.number().min(0.1).max(3),
    generatore: z.number().min(0.5).max(10),
    tempoMinimoOnMin: z.number().int().min(0).max(60),
    tempoMinimoOffMin: z.number().int().min(0).max(60),
  }),
  deltaT: z.object({
    nominale: z.number().min(1).max(30),
    sogliaAllarme: z.number().min(0.5).max(10),
    minutiPrimaDellAllarme: z.number().int().min(1).max(120),
  }),
  circolatore: z.object({
    modalita: z.enum(['FISSO', 'MODULANTE']),
    portataMinimaPct: z.number().int().min(0).max(100),
  }),
  sanitario: z.object({
    setpoint: z.number().min(35).max(65),
    isteresi: z.number().min(1).max(15),
    prioritaSuRiscaldamento: z.boolean(),
    tettoPrioritaMin: z.number().int().min(5).max(120),
    antilegionella: z.object({
      temperatura: z.number().min(55).max(70),
      giorno: z.number().int().min(0).max(6),
      ora: z.number().int().min(0).max(23),
    }),
  }),
  cascata: z.object({ ritardoSecondarioMin: z.number().int().min(1).max(120) }),
  comportamentoPin12: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)]),
  protezioni: z.object({
    antigeloZona: z.number().min(0).max(12),
    antigeloEsterna: z.number().min(-5).max(10),
    sovratemperaturaDelta: z.number().min(2).max(30),
    antibloccaggioGiorni: z.number().int().min(1).max(30),
    margineRugiada: z.number().min(0).max(6),
  }),
  commutazioneStagionale: z.object({
    sogliaRiscaldamento: z.number().min(5).max(25),
    sogliaRaffrescamento: z.number().min(15).max(35),
    ritardoOre: z.number().int().min(1).max(240),
  }),
  vmc: z.object({ sogliaUmidita: z.number().int().min(30).max(90) }),
});

export const RichiestaForzaturaSchema = z.object({
  canale: z.string().min(1),
  stato: z.enum(['attivo', 'spento']),
  modulazione: z.number().min(0).max(100).optional(),
  timeoutSec: z.number().int().min(10).max(3600),
});
